/* eslint-disable no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import { useContext, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

import { FormNavigationContext } from "../components/FormNavigation/FormNavigationProvider";
import { updateProspect } from "../store/actions/appConfig";
import { getIsEditableStatusSearchInfo } from "../store/selectors/searchProspect";
import { checkLoginStatus } from "../store/selectors/loginSelector";
import { getProspectCompletePercentage } from "../store/selectors/appConfig";

/**
 * Custom hook to calculate and update completion percentage
 * Extracted from FormNavigation component to maintain functionality
 * even when FormNavigation UI is hidden
 */
export const useCompletionPercentage = () => {
  const location = useLocation();
  const { pathname } = location;
  const navContext = useContext(FormNavigationContext);
  const dispatch = useDispatch();

  const isComeFromROScreens = useSelector(getIsEditableStatusSearchInfo);
  const isAgent = useSelector(checkLoginStatus);
  const prospectCompletePercentage = useSelector(getProspectCompletePercentage);

  const [activePath, setActivePath] = useState(undefined);
  const [activeStepIndex, setActiveStepIndex] = useState(undefined);
  const [eligibilityActiveStep, setEligibilityActiveStep] = useState(undefined);
  const [eligibilityActiveStepIndex, setEligibilityActiveStepIndex] = useState(undefined);
  const [eligibilityActivePath, setEligibilityActivePath] = useState(undefined);

  const getTotalSteps = steps => {
    if (!steps || !Array.isArray(steps)) {
      return 0;
    }
    return steps.reduce((out, inp) => {
      if (!inp?.paths || !Array.isArray(inp.paths)) {
        return out;
      }
      return out + inp.paths.length;
    }, 0);
  };

  const findMatchedPath = steps => {
    if (!steps || !Array.isArray(steps)) {
      return undefined;
    }
    return steps.find(step => (step.paths ? step.paths.some(path => pathname === path) : false));
  };

  const calculateActiveStepPercentage = (activeStep, navigationSteps, activePath) => {
    const totSteps = getTotalSteps(navigationSteps);
    const comSteps = navigationSteps.filter(item => item.step < activeStep.step);
    const totComSteps = comSteps ? getTotalSteps(comSteps) : 0;
    const completionPercent = ((totComSteps + activePath) * 100) / totSteps / 2;

    let roCompletionPercent = 0;
    if (isAgent && isComeFromROScreens) {
      roCompletionPercent = ((((totComSteps + activePath) * 100) / totSteps) * 25) / 100;
    }

    return completionPercent + roCompletionPercent;
  };

  const calculateEligibilityStepPercentage = (
    eligibilityActiveStepFound,
    eligibilityNavigationSteps,
    eligibilityActivePath
  ) => {
    const eligibilityTotSteps = getTotalSteps(eligibilityNavigationSteps);
    const eligibilityComSteps = eligibilityNavigationSteps.filter(
      item => item.step < eligibilityActiveStepFound.step
    );
    const totalEligComSteps = eligibilityComSteps ? getTotalSteps(eligibilityComSteps) : 0;
    const completedEligibilityPaths = totalEligComSteps + eligibilityActivePath + 1;
    const eligibilityCompletionPercent = (completedEligibilityPaths * 30) / eligibilityTotSteps;

    const totEligCompletePercent = eligibilityCompletionPercent + 45;

    let roCompletionPercent = 0;
    if (isAgent && isComeFromROScreens) {
      const totalRoCompPercentage = (completedEligibilityPaths * 2.5) / eligibilityTotSteps;
      roCompletionPercent = totalRoCompPercentage + 22.5;
    }

    return roCompletionPercent + totEligCompletePercent;
  };

  const updateNavigationState = (activeStep, eligibilityActiveStepFound, isShowFinanceInfo) => {
    setActiveStepIndex(activeStep?.step);
    setEligibilityActiveStep(eligibilityActiveStepFound);
    setEligibilityActiveStepIndex(eligibilityActiveStepFound?.step);

    const activePathValue = isShowFinanceInfo
      ? null
      : activeStep
      ? activeStep.paths.findIndex(path => pathname === path)
      : null;
    setActivePath(activePathValue);

    const eligibilityActivePathValue = isShowFinanceInfo
      ? null
      : eligibilityActiveStepFound
      ? eligibilityActiveStepFound.paths.findIndex(path => pathname === path)
      : null;
    setEligibilityActivePath(eligibilityActivePathValue);
  };

  useEffect(() => {
    if (!navContext) {
      return;
    }

    const [
      isShowFinanceInfo,
      _isChatVisible, // This is actually just a boolean flag
      navigationSteps = [],
      _isCollapsible, // not used in percentage calculation
      _isAgentPage, // not used in percentage calculation
      eligibilityNavigationSteps = []
    ] = navContext;

    const activeStep = findMatchedPath(navigationSteps);
    const eligibilityActiveStepFound = findMatchedPath(eligibilityNavigationSteps);

    updateNavigationState(activeStep, eligibilityActiveStepFound, isShowFinanceInfo);

    let totalPercentage = 0;

    if (activeStep) {
      totalPercentage = calculateActiveStepPercentage(activeStep, navigationSteps, activePath);
    } else if (eligibilityActiveStepFound) {
      totalPercentage = calculateEligibilityStepPercentage(
        eligibilityActiveStepFound,
        eligibilityNavigationSteps,
        eligibilityActivePath
      );
    }

    // Only update if the new percentage is higher than the current one
    if (Number(totalPercentage.toFixed(2).toString()) > Number(prospectCompletePercentage)) {
      dispatch(
        updateProspect({
          "prospect.applicationInfo.completionPercentage": totalPercentage.toFixed(2).toString()
        })
      );
    }
  }, [
    navContext,
    activePath,
    activeStepIndex,
    eligibilityActivePath,
    eligibilityActiveStepIndex,
    eligibilityActiveStep,
    prospectCompletePercentage,
    pathname,
    dispatch,
    isAgent,
    isComeFromROScreens
  ]);

  return {
    activePath,
    activeStepIndex,
    eligibilityActiveStep,
    eligibilityActiveStepIndex,
    eligibilityActivePath
  };
};
